import { taxIdLabel, taxModeFromDb } from "@talentkontor/domain";
import { currentLang, t } from "../lib/sprache";

/**
 * Steuerbegriffe nach dem Land des Ausstellers bzw. der Agentur, an EINER Stelle für Rechnungsgenerator, Exporte und Hinweise
 * (SAAS-R3-3, SAAS-R3-6): in der Schweiz und in Liechtenstein „MWST“ und „nicht mehrwertsteuerpflichtig“, sonst „Umsatzsteuer“
 * und „Kleinunternehmer“. Englisch ohne deutsche Norm, wo der Fall mehrere Länder betrifft.
 */
const isCh = (country: string | null | undefined) => {
  const c = (country ?? "").toUpperCase();
  return c === "CH" || c === "LI";
};

/** Steuerfall (Schlüssel aus der Datenbank oder der Fachlogik) in Klartext; unbekannte Werte bleiben stehen */
export function taxModeText(mode: string | null | undefined, country: string | null | undefined, recipientCountry?: string | null): string {
  const m = taxModeFromDb(mode ?? null);
  const ch = isCh(country);
  const c = (country ?? "").toUpperCase();
  // K-270: Steuerschuld beim Empfänger richtet sich nach dem Land des Empfängers (CH/LI Bezugsteuer, DE § 13b); ein Aussteller in
  // der Schweiz mit Empfänger im Ausland hat nie „Bezugsteuer“
  const r = (recipientCountry ?? "").toUpperCase();
  if (m === "REVERSE_CHARGE_DOMESTIC" && r) {
    return isCh(r) ? t("Bezugsteuer (der Empfänger schuldet die MWST)", "Acquisition tax (the recipient owes the VAT)")
      : r === "DE" ? t("Steuerschuld beim Empfänger (§ 13b UStG)", "Reverse charge domestic (§ 13b UStG)")
      : t("Steuerschuld beim Empfänger", "Reverse charge");
  }
  switch (m) {
    case "DOMESTIC": return ch ? t("Inland mit MWST", "Domestic with VAT (MWST)") : t("Inland mit Umsatzsteuer", "Domestic with VAT");
    case "SMALL_BUSINESS": return ch ? t("Nicht mehrwertsteuerpflichtig", "Not subject to VAT") : t("Kleinunternehmer (ohne Umsatzsteuer)", "Small business (no VAT)");
    case "REVERSE_CHARGE_DOMESTIC": return ch ? t("Bezugsteuer (der Empfänger schuldet die MWST)", "Acquisition tax (the recipient owes the VAT)")
      : c === "DE" ? t("Steuerschuld beim Empfänger (§ 13b UStG)", "Reverse charge domestic (§ 13b UStG)")
      : t("Steuerschuld beim Empfänger im Inland", "Domestic reverse charge");
    case "REVERSE_CHARGE_EU": return t("Reverse Charge EU", "Reverse charge EU");
    case "NOT_TAXABLE_ABROAD": return t("Nicht steuerbar (Leistungsort im Ausland)", "Not taxable (place of supply abroad)");
    case "MANUAL": return t("Steuerliche Prüfung", "Tax review");
    case "NOT_TAXABLE_DAMAGES": return t("Nicht steuerbar (Schadensersatz: Verzugskosten)", "Not subject to VAT (damages: late-payment costs)");
    default: return mode ?? "";
  }
}

/** Kurzes Steuerwort des Landes („Umsatzsteuer“, in der Schweiz „MWST“) */
export function vatWordOf(country: string | null | undefined): string {
  return isCh(country) ? "MWST" : t("Umsatzsteuer", "VAT");
}

/** Bezeichnung der Steuer-ID (USt-IdNr., UID-Nr., UID/MWST-Nr.) in der Oberflächensprache */
export function taxIdWord(country: string | null | undefined): string {
  return taxIdLabel(country ?? null, currentLang() === "en" ? "en" : "de");
}

/** Steuernummer in den Begriffen des Landes; die Schweiz kennt keine eigene Steuernummer neben der UID */
export function taxNumberWord(country: string | null | undefined): string {
  return isCh(country) ? t("Steuernummer (falls vorhanden)", "Tax number (if any)") : t("Steuernummer", "Tax number");
}

/** Kleinunternehmer bzw. nicht mehrwertsteuerpflichtig, ohne Norm */
export function smallBusinessWord(country: string | null | undefined, on: boolean): string {
  if (isCh(country)) return on ? t("Nicht mehrwertsteuerpflichtig", "Not subject to VAT") : t("MWST-pflichtig", "Registered for VAT");
  return on ? t("Kleinunternehmer", "Small business") : t("Regelbesteuerung", "Standard taxation");
}

/**
 * Anmeldung der einbehaltenen Abzugsteuer nach dem Sitzland der Agentur (SAAS-R3-4): Deutschland meldet elektronisch beim
 * Bundeszentralamt für Steuern an (§ 50a Abs. 5 EStG, vierteljährlich bis zum 10. nach dem Quartal), Österreich beim Finanzamt
 * (§ 100 EStG, bis zum 15. des Folgemonats). Hinweis, keine Steuerberatung.
 */
export function withholdingFilingNote(country: string | null | undefined): string {
  const c = (country ?? "").toUpperCase();
  if (c === "DE") return t("Anmeldung und Zahlung beim Bundeszentralamt für Steuern (BZSt), elektronisch und vierteljährlich bis zum 10. nach dem Quartal (§ 50a Abs. 5 EStG). Hinweis ohne Steuerberatung.",
    "File and pay with the Federal Central Tax Office (BZSt), electronically and quarterly by the 10th after the quarter (§ 50a (5) EStG). Notice, not tax advice.");
  if (c === "AT") return t("Anmeldung und Zahlung beim Finanzamt bis zum 15. des Folgemonats (§ 100 EStG, Abzugsteuer nach § 99 EStG). Hinweis ohne Steuerberatung.",
    "File and pay with the tax office by the 15th of the following month (§ 100 EStG, withholding tax under § 99 EStG). Notice, not tax advice.");
  return t("Anmeldung nach den Regeln des Sitzlands der Agentur. Hinweis ohne Steuerberatung.", "Filing under the rules of the agency's country. Notice, not tax advice.");
}
